import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import useSWRInfinite from "swr/infinite";

import { useConnectClient } from "../../utility/connectWeb";
import {
  INITIAL_TEXT_FETCH_LIMIT,
  toTextAnswerDetail,
} from "./useGetTicketQuestionAnswers";

import type {
  GetTicketQuestionAnswersError,
  TicketQuestionnaireTextAnswerDetail,
} from "./useGetTicketQuestionAnswers";

// 追加読込時の1回あたりの追加取得件数
export const LOAD_MORE_LIMIT = 20;

/**
 * 自由記述（TEXT）設問の「もっと見る」用フック
 */
export const useGetTicketQuestionTextAnswers = (
  eventIds: string[],
  questionId: string,
  snapshotAt?: bigint,
  fromDate?: bigint,
  toDate?: bigint
) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const { data, size, setSize, isValidating, error, mutate } = useSWRInfinite<
    TicketQuestionnaireTextAnswerDetail[],
    GetTicketQuestionAnswersError
  >(
    (
      pageIndex: number,
      previousPageData: TicketQuestionnaireTextAnswerDetail[] | null
    ) => {
      if (!currentUserId || uniqueEventIds.length === 0 || !questionId) {
        return null;
      }
      // 前のページが空だった場合（最後まで取得しきった場合）はリクエストしない
      if (previousPageData && previousPageData.length === 0) return null;

      // offsetは、初期取得で INITIAL_TEXT_FETCH_LIMIT (20件) 取得済みのため、そこからスタートする
      const offset = INITIAL_TEXT_FETCH_LIMIT + pageIndex * LOAD_MORE_LIMIT;

      return [
        "getTicketQuestionTextAnswers",
        currentUserId,
        uniqueEventIdsKey,
        questionId,
        fromDate?.toString() ?? "",
        toDate?.toString() ?? "",
        offset,
        LOAD_MORE_LIMIT,
        snapshotAt?.toString() ?? "",
      ] as const;
    },
    async ([
      _key,
      _uid,
      _eventIds,
      _qId,
      _from,
      _to,
      offset,
      limit,
      _snapshotAtStr,
    ]) => {
      try {
        const response = await connectClient.getTicketQuestionnaireTextAnswers({
          eventIds: uniqueEventIds,
          questionId,
          fromDate,
          toDate,
          offset: Number(offset),
          limit: Number(limit),
          snapshotAt,
        });

        return response.answerDetails.map(toTextAnswerDetail);
      } catch (error) {
        console.error(
          `Error fetching text answers for question ${questionId}:`,
          error
        );
        throw error;
      }
    },
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    }
  );

  const loadMoreAnswers = data ? data.flat() : [];
  const isLoading =
    isValidating && (!data || typeof data[size - 1] === "undefined");

  // 今回取得した件数が上限に達していなければ、次ページはないと判定
  const hasMore = data && data[data.length - 1]?.length === LOAD_MORE_LIMIT;

  return {
    data: loadMoreAnswers,
    isLoading,
    hasMore,
    size,
    setSize,
    error,
    mutate,
  };
};
